/**
 * Pure helpers for the guest-facing forms feature: wire-format coercion,
 * client-side validation, and translation of backend error codes into
 * per-question UI state. Kept free of React so the rules that decide
 * whether a form can be submitted are readable in one place.
 */

export const TRIGGER_LABELS = {
  check_in: "Before check-in",
  check_out: "Before check-out",
  general: "General",
};

export const TRIGGER_ORDER = ["check_in", "check_out", "general"];

export const normalizeTrigger = (trigger) => {
  if (!trigger) return "general";
  return String(trigger).toLowerCase().replace(/-/g, "_");
};

export const getStatus = (response) => {
  if (!response) return "not_started";
  if (response.status === "submitted") return "submitted";
  if (response.status === "skipped") return "skipped";
  return "pending";
};

export const sortQuestions = (questions = []) =>
  [...questions].sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));

export const isBlank = (value) => {
  if (value == null) return true;
  if (Array.isArray(value)) return value.length === 0;
  return String(value).trim().length === 0;
};

export const normalizeEmail = (value) => String(value ?? "").trim().toLowerCase();

export const isEmailValid = (value) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value ?? "").trim());

/**
 * The backend accepts 6–20 digits with an optional leading `+`. Guests
 * type spaces, dashes and brackets, so validation counts digits only and
 * `normalizePhone` strips the decoration before submitting.
 */
export const isPhoneValid = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return false;
  if (!/^\+?[\d\s().-]+$/.test(raw)) return false;
  const digits = raw.replace(/\D/g, "");
  return digits.length >= 6 && digits.length <= 20;
};

export const normalizePhone = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  const digits = raw.replace(/\D/g, "");
  if (!digits) return "";
  return raw.startsWith("+") ? `+${digits}` : digits;
};

/**
 * yes_no answers travel as the strings "true" / "false". Drafts written by
 * earlier builds stored "yes" / "no", so reads stay permissive while every
 * write goes out in the documented shape.
 */
export const coerceYesNo = (value) => {
  if (value === true) return "true";
  if (value === false) return "false";
  const raw = String(value ?? "").trim().toLowerCase();
  if (raw === "true" || raw === "yes") return "true";
  if (raw === "false" || raw === "no") return "false";
  return "";
};

export const coerceConsent = (value) =>
  value === true || String(value ?? "").trim().toLowerCase() === "true"
    ? "true"
    : "";

export const emptyGuestRow = () => ({ name: "", phone: "", email: "" });

export const normalizeGuestList = (value) => {
  if (!Array.isArray(value) || value.length === 0) return [emptyGuestRow()];
  return value.map((entry) => ({
    name: String(entry?.name ?? ""),
    phone: String(entry?.phone ?? ""),
    email: String(entry?.email ?? ""),
  }));
};

const isGuestRowEmpty = (row) =>
  isBlank(row?.name) && isBlank(row?.phone) && isBlank(row?.email);

/** Fully-empty rows are UI scaffolding, never sent to the backend. */
export const compactGuestList = (rows = []) =>
  rows.filter((row) => !isGuestRowEmpty(row));

export const isAnswered = (question, value) => {
  switch (question?.type) {
    case "consent_checkbox":
      return coerceConsent(value) === "true";
    case "guest_list":
      return compactGuestList(normalizeGuestList(value)).some(
        (row) => !isBlank(row.name)
      );
    case "yes_no":
      return coerceYesNo(value) !== "";
    default:
      return !isBlank(value);
  }
};

export const isEmailVerified = (question, value, verifiedEmails = {}) => {
  if (!question?.requireVerification) return true;
  const answer = normalizeEmail(value);
  if (!answer) return false;
  return normalizeEmail(verifiedEmails[question.id]) === answer;
};

export const normalizeVerifiedEmails = (verifiedEmails) => {
  if (!verifiedEmails || typeof verifiedEmails !== "object") return {};
  const map = {};
  for (const [questionID, email] of Object.entries(verifiedEmails)) {
    if (email) map[questionID] = String(email);
  }
  return map;
};

const normalizeIncomingValue = (type, value) => {
  switch (type) {
    case "yes_no":
      return coerceYesNo(value);
    case "consent_checkbox":
      return coerceConsent(value);
    case "guest_list":
      return normalizeGuestList(value);
    case "multi_choice":
      if (Array.isArray(value)) return value.map((v) => String(v));
      return isBlank(value) ? [] : [String(value)];
    default:
      if (value == null) return "";
      return Array.isArray(value) ? value : String(value);
  }
};

/** Saved answers[] → the editor's `{ [questionID]: value }` working copy. */
export const answersToMap = (answers = [], questions = []) => {
  const typeById = {};
  for (const question of questions) {
    if (question?.id) typeById[question.id] = question.type;
  }
  const map = {};
  for (const answer of answers) {
    if (!answer || answer.questionID == null) continue;
    map[answer.questionID] = normalizeIncomingValue(
      typeById[answer.questionID],
      answer.value
    );
  }
  return map;
};

/**
 * Working copy → wire `answers[]`. Driven by the form definition rather
 * than the answer map so answers to questions the host has since removed
 * are dropped instead of tripping UNKNOWN_QUESTION, and so every value
 * leaves in its documented type.
 */
export const buildAnswerPayload = (answers = {}, questions = []) => {
  const payload = [];
  for (const question of questions) {
    if (!question?.id) continue;
    const value = answers[question.id];

    switch (question.type) {
      case "guest_list": {
        const rows = compactGuestList(normalizeGuestList(value)).map((row) => {
          const entry = { name: String(row.name ?? "").trim() };
          const phone = normalizePhone(row.phone);
          const email = String(row.email ?? "").trim();
          if (phone) entry.phone = phone;
          if (email) entry.email = email;
          return entry;
        });
        if (rows.length) payload.push({ questionID: question.id, value: rows });
        break;
      }
      case "multi_choice": {
        const selected = Array.isArray(value) ? value.map((v) => String(v)) : [];
        if (selected.length) {
          payload.push({ questionID: question.id, value: selected });
        }
        break;
      }
      case "yes_no": {
        const answer = coerceYesNo(value);
        if (answer) payload.push({ questionID: question.id, value: answer });
        break;
      }
      case "consent_checkbox": {
        if (coerceConsent(value) === "true") {
          payload.push({ questionID: question.id, value: "true" });
        }
        break;
      }
      case "phone": {
        const phone = normalizePhone(value);
        if (phone) payload.push({ questionID: question.id, value: phone });
        break;
      }
      case "email": {
        const email = String(value ?? "").trim();
        if (email) payload.push({ questionID: question.id, value: email });
        break;
      }
      default: {
        if (isBlank(value)) break;
        payload.push({ questionID: question.id, value: String(value).trim() });
      }
    }
  }
  return payload;
};

/**
 * Bookings created from a PMS often carry a synthetic `@noemail.com`
 * address; showing it back to the guest as their email is worse than
 * showing nothing.
 */
export const cleanGuestEmail = (email) => {
  const value = String(email ?? "").trim();
  return value.toLowerCase().includes("@noemail.com") ? "" : value;
};

/**
 * Seeds the first row of guest_list questions with the primary guest's
 * details. Only fills rows that are still completely empty, so a saved
 * draft is never overwritten.
 */
export const applyGuestPrefill = (answers = {}, questions = [], guest) => {
  if (!guest) return answers;
  let next = answers;
  for (const question of questions) {
    if (question?.type !== "guest_list" || !question.id) continue;
    if (compactGuestList(normalizeGuestList(answers[question.id])).length) {
      continue;
    }
    const prefilled = {
      name: String(guest.name ?? "").trim(),
      phone: String(guest.phone ?? "").trim(),
      email: cleanGuestEmail(guest.email),
    };
    if (isBlank(prefilled.name) && isBlank(prefilled.phone) && isBlank(prefilled.email)) {
      continue;
    }
    if (next === answers) next = { ...answers };
    next[question.id] = [prefilled];
  }
  return next;
};

/**
 * Front-line validation so the backend's 400s are a safety net rather than
 * the primary experience. Returns per-question messages plus, for guest
 * lists, a `{ [questionID]: { [rowIndex]: { field: message } } }` map.
 */
export const validateAnswers = ({
  questions = [],
  answers = {},
  verifiedEmails = {},
}) => {
  const errors = {};
  const guestListErrors = {};

  for (const question of questions) {
    if (!question?.id) continue;
    const value = answers[question.id];

    // Consent is required by definition, whatever the form says.
    if (question.type === "consent_checkbox") {
      if (coerceConsent(value) !== "true") {
        errors[question.id] = "You need to agree to this to continue.";
      }
      continue;
    }

    if (question.type === "guest_list") {
      const rows = compactGuestList(normalizeGuestList(value));
      if (question.required && rows.length === 0) {
        errors[question.id] = "Add at least one guest.";
        continue;
      }
      const rowErrors = {};
      rows.forEach((row, index) => {
        const fieldErrors = {};
        if (isBlank(row.name)) fieldErrors.name = "Name is required.";
        if (!isBlank(row.email) && !isEmailValid(row.email)) {
          fieldErrors.email = "Enter a valid email.";
        }
        if (!isBlank(row.phone) && !isPhoneValid(row.phone)) {
          fieldErrors.phone = "Enter a valid phone number.";
        }
        if (Object.keys(fieldErrors).length) rowErrors[index] = fieldErrors;
      });
      if (Object.keys(rowErrors).length) {
        guestListErrors[question.id] = rowErrors;
        errors[question.id] = "Check the highlighted guest details.";
      }
      continue;
    }

    if (question.required && !isAnswered(question, value)) {
      errors[question.id] = "Required";
      continue;
    }

    if (isBlank(value)) continue;

    if (question.type === "email") {
      if (!isEmailValid(value)) {
        errors[question.id] = "Enter a valid email address.";
      } else if (!isEmailVerified(question, value, verifiedEmails)) {
        errors[question.id] = "Verify this email address to continue.";
      }
      continue;
    }

    if (question.type === "phone" && !isPhoneValid(value)) {
      errors[question.id] = "Enter a valid phone number.";
    }
  }

  const firstErrorId =
    questions.find((question) => errors[question?.id])?.id || null;

  return {
    errors,
    guestListErrors,
    firstErrorId,
    isValid: Object.keys(errors).length === 0,
  };
};

const GUEST_LIST_FIELD_MESSAGES = {
  name: "Name is required.",
  phone: "Enter a valid phone number.",
  email: "Enter a valid email.",
};

/**
 * Backend validation failures arrive as 400s carrying `{ code, details }`.
 * Translate them into the same shape `validateAnswers` produces so the UI
 * highlights fields identically no matter which side caught the problem.
 */
export const mapSubmitError = (error, questions = []) => {
  const payload = error?.response?.data || {};
  const details = payload.details || {};
  const result = {
    code: payload.code || null,
    errors: {},
    guestListErrors: {},
    focusId: null,
    verifyQuestionId: null,
    shouldRefetch: false,
    message:
      payload.error ||
      error?.message ||
      "Couldn't submit the form. Please try again.",
  };

  switch (payload.code) {
    case "MISSING_REQUIRED": {
      const missing = Array.isArray(details.missingQuestionIds)
        ? details.missingQuestionIds
        : [];
      for (const id of missing) result.errors[id] = "Required";
      result.focusId =
        questions.find((question) => missing.includes(question?.id))?.id ||
        missing[0] ||
        null;
      result.message = "Please fill in the highlighted questions.";
      break;
    }
    case "INVALID_EMAIL": {
      if (details.questionID) {
        result.errors[details.questionID] = "Enter a valid email address.";
        result.focusId = details.questionID;
      }
      result.message = "Please check the email address you entered.";
      break;
    }
    case "INVALID_PHONE": {
      if (details.questionID) {
        result.errors[details.questionID] =
          "Enter a valid phone number (6–20 digits).";
        result.focusId = details.questionID;
      }
      result.message = "Please check the phone number you entered.";
      break;
    }
    case "INVALID_GUEST_LIST": {
      const questionID =
        details.questionID ||
        questions.find((question) => question?.type === "guest_list")?.id;
      const index = Number(details.entryIndex);
      if (questionID && Number.isFinite(index)) {
        const field = details.field || "name";
        result.guestListErrors[questionID] = {
          [index]: {
            [field]:
              GUEST_LIST_FIELD_MESSAGES[field] || "Please check this field.",
          },
        };
        result.errors[questionID] = "Check the highlighted guest details.";
        result.focusId = questionID;
      }
      result.message = "Please check the guest details you entered.";
      break;
    }
    case "CONSENT_REQUIRED": {
      const consentIds = details.questionID
        ? [details.questionID]
        : questions
            .filter((question) => question?.type === "consent_checkbox")
            .map((question) => question.id);
      for (const id of consentIds) {
        result.errors[id] = "You need to agree to this to continue.";
      }
      result.focusId = consentIds[0] || null;
      result.message = "Please agree to continue.";
      break;
    }
    case "EMAIL_NOT_VERIFIED": {
      if (details.questionID) {
        result.errors[details.questionID] =
          "Verify this email address to continue.";
        result.focusId = details.questionID;
        result.verifyQuestionId = details.questionID;
      }
      result.message = "Please verify your email address to continue.";
      break;
    }
    case "FORM_NOT_FOUND":
    case "UNKNOWN_QUESTION": {
      result.shouldRefetch = true;
      result.message = "This form was just updated. Reloading the latest version.";
      break;
    }
    default:
      break;
  }

  return result;
};

/** Friendly copy for the verify-email send/confirm error codes. */
export const describeVerificationError = (error) => {
  const payload = error?.response?.data || {};
  const details = payload.details || {};

  switch (payload.code) {
    case "RESEND_COOLDOWN": {
      const seconds = Number(details.retryInSeconds);
      return Number.isFinite(seconds) && seconds > 0
        ? `Please wait ${seconds}s before requesting another code.`
        : "Please wait a moment before requesting another code.";
    }
    case "CODE_MISMATCH": {
      const left = Number(details.attemptsRemaining);
      if (Number.isFinite(left) && left > 0) {
        return `That code isn't right. ${left} ${
          left === 1 ? "attempt" : "attempts"
        } left.`;
      }
      return "That code isn't right.";
    }
    case "CODE_EXPIRED":
      return "That code has expired. Request a new one.";
    case "TOO_MANY_ATTEMPTS":
      return "Too many incorrect attempts. Request a new code to try again.";
    case "NO_PENDING_VERIFICATION":
      return "No code is pending for this address. Send a new one.";
    case "INVALID_EMAIL":
      return "That email address doesn't look valid.";
    default:
      return (
        payload.error ||
        error?.message ||
        "Something went wrong. Please try again."
      );
  }
};

/** Codes that invalidate the pending code, sending the guest back to "send". */
export const CLEARS_PENDING_CODE = new Set([
  "CODE_EXPIRED",
  "TOO_MANY_ATTEMPTS",
  "NO_PENDING_VERIFICATION",
]);
